import type {
  AccountType,
  IdentityBadge,
  IdentityEntitlements,
  IdentityRecord,
  IdentityRequestKind,
  IdentityRequestStatus,
  LegacyAccountType,
  PremiumEntitlement,
  PublicIdentitySnapshot,
  VerificationStatus,
} from "../types/identity";

export type IdentitySource = "accountType" | "legacy-role" | "unknown";
export type VerificationSource = "verificationStatus" | "legacy-teacherVerified" | "default";

export type NormalizedIdentity = {
  accountType: AccountType | null;
  accountTypeSource: IdentitySource;
  verificationStatus: VerificationStatus;
  verificationSource: VerificationSource;
  entitlements?: IdentityEntitlements;
};

export type AuthorIdentitySnapshot = {
  authorIdentityId?: unknown;
  authorSnapshot?: unknown;
  authorAccountType?: unknown;
  authorRole?: unknown;
  authorVerificationStatus?: unknown;
  authorEntitlements?: unknown;
  teacherVerified?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isAccountType(value: unknown): value is AccountType {
  return value === "student" || value === "teacher" || value === "institution";
}

export function isLegacyAccountType(value: unknown): value is LegacyAccountType {
  return value === "student" || value === "teacher";
}

export function parseAccountType(value: unknown): AccountType | null {
  return isAccountType(value) ? value : null;
}

export function parseLegacyAccountType(value: unknown): LegacyAccountType | null {
  return isLegacyAccountType(value) ? value : null;
}

export function isVerificationStatus(value: unknown): value is VerificationStatus {
  return value === "unverified"
    || value === "pending"
    || value === "verified"
    || value === "rejected";
}

export function parseVerificationStatus(value: unknown): VerificationStatus | null {
  return isVerificationStatus(value) ? value : null;
}

export function isIdentityRequestKind(value: unknown): value is IdentityRequestKind {
  return value === "teacher_verification"
    || value === "institution_identity"
    || value === "institution_conversion";
}

export function isIdentityRequestStatus(value: unknown): value is IdentityRequestStatus {
  return value === "pending"
    || value === "approved"
    || value === "rejected"
    || value === "cancelled";
}

export function isPremiumEntitlementStatus(value: unknown): value is PremiumEntitlement["status"] {
  return value === "active" || value === "inactive";
}

function normalizePremiumEntitlement(value: unknown): PremiumEntitlement | undefined {
  if (!isRecord(value) || !isPremiumEntitlementStatus(value.status)) return undefined;

  return {
    status: value.status,
    ...(typeof value.source === "string" ? { source: value.source } : {}),
    ...(value.expiresAt !== undefined ? { expiresAt: value.expiresAt } : {}),
  };
}

export function normalizeEntitlements(value: unknown): IdentityEntitlements | undefined {
  if (!isRecord(value)) return undefined;

  const premium = normalizePremiumEntitlement(value.premium);
  return premium ? { premium } : undefined;
}

export function normalizeLegacyIdentity(
  profile: IdentityRecord | null | undefined
): NormalizedIdentity {
  const canonicalAccountType = parseAccountType(profile?.accountType);
  const legacyAccountType = parseLegacyAccountType(profile?.role);
  const accountType = canonicalAccountType ?? legacyAccountType;

  const canonicalVerificationStatus = parseVerificationStatus(profile?.verificationStatus);
  const legacyVerified = accountType === "teacher" && profile?.teacherVerified === true;
  const verificationStatus = canonicalVerificationStatus
    ?? (legacyVerified ? "verified" : "unverified");

  return {
    accountType,
    accountTypeSource: canonicalAccountType
      ? "accountType"
      : legacyAccountType
        ? "legacy-role"
        : "unknown",
    verificationStatus,
    verificationSource: canonicalVerificationStatus
      ? "verificationStatus"
      : legacyVerified
        ? "legacy-teacherVerified"
        : "default",
    entitlements: normalizeEntitlements(profile?.entitlements),
  };
}

/** Canonical name used by new callers; the legacy alias remains explicit above. */
export const normalizeIdentity = normalizeLegacyIdentity;

export function normalizeAuthorIdentity(
  snapshot: AuthorIdentitySnapshot | null | undefined
): NormalizedIdentity {
  const publicSnapshot = isRecord(snapshot?.authorSnapshot) ? snapshot.authorSnapshot : null;
  return normalizeLegacyIdentity({
    accountType: publicSnapshot?.accountType ?? snapshot?.authorAccountType,
    role: snapshot?.authorRole,
    verificationStatus: snapshot?.authorVerificationStatus,
    entitlements: snapshot?.authorEntitlements,
    teacherVerified: snapshot?.teacherVerified,
  });
}

export function getAuthorAccountType(
  snapshot: AuthorIdentitySnapshot | null | undefined
): AccountType | null {
  const publicSnapshot = isRecord(snapshot?.authorSnapshot) ? snapshot.authorSnapshot : null;
  if (isAccountType(publicSnapshot?.accountType)) return publicSnapshot.accountType;
  return normalizeAuthorIdentity(snapshot).accountType;
}

export function getAuthorIdentityId(
  snapshot: AuthorIdentitySnapshot | null | undefined
) {
  if (typeof snapshot?.authorIdentityId === "string" && snapshot.authorIdentityId.trim()) {
    return snapshot.authorIdentityId.trim();
  }
  const publicSnapshot = isRecord(snapshot?.authorSnapshot) ? snapshot.authorSnapshot : null;
  if (typeof publicSnapshot?.identityId === "string" && publicSnapshot.identityId.trim()) {
    return publicSnapshot.identityId.trim();
  }
  const legacyUid = (snapshot as { authorUid?: unknown } | null | undefined)?.authorUid;
  return typeof legacyUid === "string" && legacyUid.trim() ? legacyUid.trim() : null;
}

/**
 * Keep the legacy authorUid query compatible without mixing a controller's
 * institution posts into its personal profile. New posts are addressed by
 * authorIdentityId; old posts may only be distinguishable by their legacy
 * author type or an already-captured public snapshot.
 */
export function isLegacyPostForPersonalIdentity(
  snapshot: AuthorIdentitySnapshot | null | undefined,
  identityId: string
) {
  if (getAuthorAccountType(snapshot) === "institution") return false;

  const explicitIdentityId = typeof snapshot?.authorIdentityId === "string"
    && snapshot.authorIdentityId.trim()
    ? snapshot.authorIdentityId.trim()
    : (() => {
      const publicSnapshot = isRecord(snapshot?.authorSnapshot) ? snapshot.authorSnapshot : null;
      return typeof publicSnapshot?.identityId === "string" && publicSnapshot.identityId.trim()
        ? publicSnapshot.identityId.trim()
        : null;
    })();

  return explicitIdentityId ? explicitIdentityId === identityId : true;
}

export function getAuthorHandle(
  snapshot: AuthorIdentitySnapshot | null | undefined
) {
  const publicSnapshot = isRecord(snapshot?.authorSnapshot) ? snapshot.authorSnapshot : null;
  if (typeof publicSnapshot?.handle === "string") return publicSnapshot.handle.trim();
  const legacyHandle = (snapshot as { authorUsername?: unknown } | null | undefined)?.authorUsername;
  return typeof legacyHandle === "string" ? legacyHandle.trim().replace(/^@/, "") : "";
}

export function getAuthorDisplayName(
  snapshot: AuthorIdentitySnapshot | null | undefined
) {
  const publicSnapshot = isRecord(snapshot?.authorSnapshot) ? snapshot.authorSnapshot : null;
  if (typeof publicSnapshot?.displayName === "string" && publicSnapshot.displayName.trim()) {
    return publicSnapshot.displayName.trim();
  }
  const handle = getAuthorHandle(snapshot);
  if (handle) return handle;
  const email = (snapshot as { authorEmail?: unknown } | null | undefined)?.authorEmail;
  if (typeof email === "string" && email.includes("@")) return email.split("@")[0] || "Utilisateur";
  return "Utilisateur";
}

export function getAuthorAvatarUrl(
  snapshot: AuthorIdentitySnapshot | null | undefined
) {
  const publicSnapshot = isRecord(snapshot?.authorSnapshot) ? snapshot.authorSnapshot : null;
  if (typeof publicSnapshot?.avatarUrl === "string" && publicSnapshot.avatarUrl.trim()) {
    return publicSnapshot.avatarUrl.trim();
  }
  const legacyPhoto = (snapshot as { authorPhotoURL?: unknown } | null | undefined)?.authorPhotoURL;
  return typeof legacyPhoto === "string" && legacyPhoto.trim() ? legacyPhoto.trim() : null;
}

function isIdentityBadge(value: unknown): value is IdentityBadge {
  return isRecord(value)
    && typeof value.kind === "string"
    && typeof value.label === "string";
}

export function getAccountType(profile: IdentityRecord | null | undefined): AccountType | null {
  return normalizeLegacyIdentity(profile).accountType;
}

export function getVerificationStatus(
  profile: IdentityRecord | null | undefined
): VerificationStatus {
  return normalizeLegacyIdentity(profile).verificationStatus;
}

export function isStudent(profile: IdentityRecord | null | undefined) {
  return getAccountType(profile) === "student";
}

export function isTeacher(profile: IdentityRecord | null | undefined) {
  return getAccountType(profile) === "teacher";
}

export function isInstitution(profile: IdentityRecord | null | undefined) {
  return getAccountType(profile) === "institution";
}

export function isVerified(profile: IdentityRecord | null | undefined) {
  return getVerificationStatus(profile) === "verified";
}

export function hasPremiumEntitlement(profile: IdentityRecord | null | undefined) {
  return normalizeLegacyIdentity(profile).entitlements?.premium?.status === "active";
}

/** PDF/document publishing is a capability, not an admin or premium permission. */
export function canPublishDocuments(profile: IdentityRecord | null | undefined) {
  const accountType = getAccountType(profile);
  return accountType === "teacher" || accountType === "institution";
}

export function canAuthorPublishDocuments(snapshot: AuthorIdentitySnapshot | null | undefined) {
  const accountType = getAuthorAccountType(snapshot);
  return accountType === "teacher" || accountType === "institution";
}

export function getIdentityBadges(
  profile: IdentityRecord | null | undefined
): IdentityBadge[] {
  const identity = normalizeLegacyIdentity(profile);
  if (!identity.accountType) return [];

  if (identity.accountType === "student") {
    return [{ kind: "student", label: "Étudiant" }];
  }

  if (identity.accountType === "teacher") {
    const badges: IdentityBadge[] = [
      identity.verificationStatus === "verified"
        ? { kind: "teacher_verified", label: "Prof vérifié" }
        : { kind: "teacher", label: "Prof" },
    ];
    if (identity.entitlements?.premium?.status === "active") {
      badges.push({ kind: "teacher_premium", label: "Prof premium" });
    }
    return badges;
  }

  return identity.entitlements?.premium?.status === "active"
    ? [{ kind: "institution_premium", label: "École Premium" }]
    : [{ kind: "institution", label: "École / Institution" }];
}

export function getPrimaryIdentityBadge(
  profile: IdentityRecord | null | undefined
): IdentityBadge | null {
  return getIdentityBadges(profile)[0] ?? null;
}

export function getAuthorIdentityBadges(
  snapshot: AuthorIdentitySnapshot | null | undefined
): IdentityBadge[] {
  const publicSnapshot = isRecord(snapshot?.authorSnapshot) ? snapshot.authorSnapshot : null;
  if (Array.isArray(publicSnapshot?.badges)) {
    const badges = publicSnapshot.badges.filter(isIdentityBadge);
    if (badges.length > 0) return badges;
  }
  return getIdentityBadges(normalizeAuthorIdentity(snapshot));
}

export function buildPublicIdentitySnapshot(account: {
  accountId: string;
  accountType: AccountType;
  displayName: string;
  username?: string;
  photoURL?: string;
  verificationStatus?: VerificationStatus;
  entitlements?: IdentityEntitlements;
  badges?: IdentityBadge[];
}): PublicIdentitySnapshot {
  const approvedBadges = Array.isArray(account.badges)
    ? account.badges.filter(isIdentityBadge)
    : [];
  return {
    identityId: account.accountId,
    handle: account.username?.trim() ?? "",
    displayName: account.displayName,
    avatarUrl: account.photoURL?.trim() ? account.photoURL.trim() : null,
    accountType: account.accountType,
    badges: approvedBadges.length > 0 ? approvedBadges : getIdentityBadges(account),
  };
}

export function getPublicAuthorSnapshot(
  snapshot: AuthorIdentitySnapshot | null | undefined
): PublicIdentitySnapshot | null {
  const identityId = getAuthorIdentityId(snapshot);
  const accountType = getAuthorAccountType(snapshot);
  if (!identityId || !accountType) return null;

  const badges = getAuthorIdentityBadges(snapshot);
  return {
    identityId,
    handle: getAuthorHandle(snapshot),
    displayName: getAuthorDisplayName(snapshot),
    avatarUrl: getAuthorAvatarUrl(snapshot),
    accountType,
    badges,
  };
}
